import { Injectable, InjectionToken, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivationEnd, Event, Router } from '@angular/router';
import { RouteData } from '@shared/utils';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export interface PageMetaData {
  title?: string;
  description?: string;
}

export const BASE_TITLE_TOKEN = new InjectionToken<string>('BaseTitleToken');

/**
 * This service simply takes "data" property on each route the user visited and
 * sets the given value as Meta title information.
 */
@Injectable()
export class MetaService {
  private _title = inject(Title);
  private router = inject(Router);
  private _meta = inject(Meta);
  private _baseTitle = inject(BASE_TITLE_TOKEN, { optional: true }) ?? '';

  init() {
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof ActivationEnd),
        map((event: Event) => (event as ActivationEnd).snapshot.data as RouteData),
        distinctUntilChanged(isEqual)
      )
      .subscribe((routeData) => {
        if (routeData.pageTitle && this.getData(routeData)) {
          this.set({
            title: this.getData(routeData),
          });
        }
      });
  }

  set(meta: PageMetaData) {
    const fullTitle = meta.title ? meta.title + (this._baseTitle ? ' – ' + this._baseTitle : '') : this._baseTitle;

    this._title.setTitle(fullTitle);

    if (meta.description) {
      this._meta.updateTag({
        content: meta.description,
        name: 'description',
      });
    }
  }

  private getData(data: RouteData) {
    // Page title can be defined as a static string or as a function
    if (data.pageTitle) return typeof data.pageTitle === 'function' ? data.pageTitle(data) : data.pageTitle;
    return '';
  }
}
